import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import Navbar from './Navbar';
import { isFoodlesSaturdayOffer, calculateDiscount } from '../utils/dateUtils';

const OfferMenu = () => {
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadOfferData = async () => {
      try {
        console.log('Loading offer zone data');
        setIsLoaded(false);

        const response = await fetch('/data/offerzone.csv');
        if (!response.ok) {
          throw new Error(`Failed to load offers (Status: ${response.status})`);
        }

        const csvText = await response.text();
        if (!csvText.trim()) {
          throw new Error('Offer data is empty');
        }
        
        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          error: (error) => {
            console.error('CSV parsing error:', error);
            setError('Failed to parse offer data');
          },
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              console.log('Parsed offer data:', results.data);
              const parsedMenuItems = results.data.map(item => ({
                ...item,
                price: parseFloat(item.price) || 0,
                quantity: 0
              }));
              
              // Extract unique categories
              const uniqueCategories = [...new Set(parsedMenuItems.map(item => item.category))];
              setCategories(uniqueCategories);
              setActiveCategory(uniqueCategories[0]); // Set first category as active
              setMenuItems(parsedMenuItems);
              setIsLoaded(true);
            } else {
              setError('No offers found');
            }
          }
        });
      } catch (error) {
        console.error('Error loading offer data:', error);
        setError(error.message);
        setIsLoaded(true);
      }
    };

    loadOfferData();
  }, []);

  const handleAddToCart = (itemId) => {
    setMenuItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, quantity: (item.quantity || 0) + 1 }
          : item
      )
    );
    setCartItems(prevCart => {
      const existingItem = prevCart.find(item => item.id === itemId);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === itemId
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      const newItem = menuItems.find(item => item.id === itemId);
      return [...prevCart, { ...newItem, quantity: 1 }];
    });
  };

  const handleRemoveFromCart = (itemId) => {
    setMenuItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId && item.quantity > 0
          ? { ...item, quantity: item.quantity - 1 }
          : item
      )
    );
    setCartItems(prevCart => {
      const updatedCart = prevCart.map(item =>
        item.id === itemId
          ? { ...item, quantity: item.quantity - 1 }
          : item
      );
      return updatedCart.filter(item => item.quantity > 0);
    });
  };

  // Simplified cart total calculation - just sum up items with discount
  const calculateCartTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0) * 0.90; // Apply 10% discount to total
  };

  const renderCartBreakdown = () => {
    if (!cartItems.length) return null;

    const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
    const discount = subtotal * 0.10; // 10% discount
    const finalTotal = subtotal - discount;

    return (
      <div className="text-xs space-y-2">
        <div className="space-y-1 text-white/70">
          {cartItems.map(item => (
            <div key={item.id} className="flex justify-between">
              <span>{item.quantity}x {item.name}</span>
              <span>₹{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-2 space-y-1">
          <div className="flex justify-between text-white/70">
            <span>Subtotal:</span>
            <span>₹{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-purple-400">
            <span>FOODLES_SATURDAY Discount:</span>
            <span>-₹{discount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between font-bold text-white">
            <span>Final Total:</span>
            <span>₹{finalTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Show a more user-friendly loading state
  if (!isLoaded) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-3 h-3 bg-green-400 rounded-full animate-ping mx-auto"/>
          <p className="text-white font-mono">Loading menu...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-500 font-mono">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="text-green-400 underline hover:text-green-300"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleProceedToCart = () => {
    const total = calculateCartTotal();
    if (total >= 180) {
      navigate('/campus1', { 
        state: { 
          cartItems,
          subtotal: total,
          isOfferZoneOrder: true,
          vendorEmail: 'yogeshthakur03839@gmail.com',
          vendorPhone: '+918278803839',
          restaurantId: '2',
          restaurantName: 'HIMALAYAN_CAFE',
          fromOfferZone: true  // Add this flag
        } 
      });
    }
  };

  return (
    <>
      <Navbar />
      <div className="bg-black min-h-screen p-1 sm:p-4 pb-32 relative">
        <div className="pt-24">
          {/* Background patterns */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[linear-gradient(45deg,#242_1px,transparent_1px),linear-gradient(-45deg,#111_1px,transparent_1px)] bg-[size:20px_20px]" />
            <div className="absolute inset-0 bg-[linear-gradient(0deg,#222_1px,transparent_1px),linear-gradient(90deg,#111_1px,transparent_1px)] bg-[size:40px_40px] opacity-50" />
          </div>

          <div className="max-w-7xl mx-auto relative mt-0">
            {/* Header */}
            <div className="mb-6">
              <div className="relative h-16 mb-2 border border-green-500/20">
                <div className="absolute inset-0 bg-black/90" />
                <h1 className="absolute inset-0 flex items-center px-6 text-2xl font-aerial text-green-400">
                | FOODLES_SATURDAY 
                </h1>
              </div>
              <div className="text-purple-400 text-sm mt-2 px-6">
                
              </div>
            </div>

            {/* Category Navigation */}
            <div className="relative mb-6">
              <div className="flex space-x-3 overflow-x-auto pb-2">
                {categories.map((category) => (
                  <button 
                    key={category}
                    className={`px-6 py-2 rounded-none transition-all duration-300 border whitespace-nowrap ${
                      activeCategory === category 
                        ? 'bg-purple-500 text-black border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.3)]'
                        : 'bg-black border-purple-500/50 text-purple-500 hover:bg-purple-500/10'
                    }`}
                    onClick={() => setActiveCategory(category)}
                  >
                    <span className="font-mono text-xs">{category.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Info Banner */}
            <div className="mb-4 text-purple-500 text-sm font-mono bg-purple-500/5 border border-purple-500/20 p-3">
              🎁 Saturday Special: Get 10% OFF on all orders.
            </div>

            {/* Menu Grid - filtered by category */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {menuItems
                .filter(item => !activeCategory || item.category === activeCategory)
                .map((item) => (
                  <div key={item.id} 
                    className="relative border border-purple-500/30 bg-purple-500/5 hover:border-purple-500 transition-all duration-300"
                  >
                    {/* Item Info */}
                    <div className="p-6 space-y-4">
                      <div className="flex justify-between items-start">
                        <h3 className="text-xl font-mono text-white">{item.name}</h3>
                        <div className="text-right">
                          <div className="text-gray-400 line-through text-sm">₹{item.price}</div>
                          <div className="text-purple-500 font-mono">₹{calculateDiscount(item.price).toFixed(2)}</div>
                        </div>
                      </div>

                      <p className="text-white/70 text-sm">{item.description}</p>

                      <div className="text-xs text-purple-400/70 mt-1">
                      
                      </div>

                      {/* Add to Cart Controls */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => handleRemoveFromCart(item.id)}
                            className="w-8 h-8 border border-white/30 text-white flex items-center justify-center hover:bg-white/10"
                            disabled={!item.quantity}
                          >
                            -
                          </button>
                          <span className="text-white font-mono">{item.quantity || 0}</span>
                          <button
                            onClick={() => handleAddToCart(item.id)}
                            className="w-8 h-8 border border-white/30 text-white flex items-center justify-center hover:bg-white/10"
                          >
                            +
                          </button>
                        </div>
                        {item.quantity > 0 && (
                          <div className="text-green-400 font-mono">
                            ₹{(item.price * item.quantity).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Decorative corners */}
                    <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-white/30" />
                    <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-white/30" />
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-white/30" />
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-white/30" />
                    
                  </div>
                ))}
            </div>

            {/* Cart Summary */}
            {cartItems.length > 0 && (
              <div className="fixed bottom-0 left-0 right-0 bg-black/95 border-t border-white/20 p-4">
                <div className="max-w-7xl mx-auto">
                  {renderCartBreakdown()}
                  <div className="flex justify-between items-center mt-4">
                    {calculateCartTotal() < 180 && (
                      <div className="text-red-400 text-xs font-mono">
                        Add ₹{(180 - calculateCartTotal()).toFixed(2)} more to proceed
                      </div>
                    )}
                    <button 
                      className={`px-8 py-3 font-mono transition-colors ${
                        calculateCartTotal() >= 180
                          ? 'bg-green-400 text-black hover:bg-green-300'
                          : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      }`}
                      onClick={handleProceedToCart}
                    >
                      PROCEED TO CART
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OfferMenu;